import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader, RefreshCw, SearchCheck } from "lucide-react";
import { useConsumerHouseReconciliation } from "../hooks/useTreasury";
import { useCollapsed } from "../hooks/useCollapsed";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";

const fmt = (n) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const cell = (item, currency) => (item.count > 0 ? `${item.count} (${formatCurrencyAmount(item.amount, currency)})` : "-");

// Read-only. A gap between consumers and the house merchant is not always a
// bug: fees and spread Novacrust keeps, and rewards it funds itself, leave
// the two sides apart on purpose. The columns on the right are the movements
// that SHOULD have reached the house merchant but did not - what is left of
// the gap after them is the part nobody has explained yet.
// One line per currency and flow that needs a person, with what to do about
// it. Nothing here is a partner conversion: the two sides of this check are
// both inside Novacrust, so the fix is to find and complete the missed
// movement, not to convert at a partner.
const investigateItems = (rows) => {
  const items = [];
  for (const r of rows) {
    const u = r.unmirrored;
    if (u.conversionCount > 0 && (u.mirrorFailureReasons ?? []).length > 0)
      items.push({
        key: `${r.currency}-conv-reasons`,
        currency: r.currency,
        text: `Why conversions are still not mirrored: ${u.mirrorFailureReasons.map((f) => `${f.reason} (${f.count})`).join("; ")}.`,
      });
    if ((u.conversionDrift?.shortCount ?? 0) > 0)
      items.push({
        key: `${r.currency}-drift`,
        currency: r.currency,
        text: `${u.conversionDrift.shortCount} mirrored conversion(s) credited the house merchant ${formatCurrencyAmount(u.conversionDrift.shortAmount, r.currency)} less than the consumer received (the rate moved between the two steps).`,
      });
    if (u.conversionCount > 0)
      items.push({
        key: `${r.currency}-conv`,
        currency: r.currency,
        text: `${u.conversionCount} consumer conversion(s) have no house-merchant counterpart (net ${formatCurrencyAmount(u.conversionNet, r.currency)}). These should have been mirrored automatically since 4 October; check why.`,
        text: `${u.conversionCount} consumer conversion(s) have no house-merchant counterpart (net ${formatCurrencyAmount(u.conversionNet, r.currency)}). New ones sync once the conversion mirror is live; older ones need the opening reconciliation.`,
      });
    if (u.septlyDepositsCredited.count > 0)
      items.push({
        key: `${r.currency}-septly`,
        currency: r.currency,
        text: `${u.septlyDepositsCredited.count} Septly deposit(s) (${formatCurrencyAmount(u.septlyDepositsCredited.amount, r.currency)}) credited to consumers but missing on the house merchant. Run the Septly backfill (dry run first).`,
      });
    if (u.septlyDepositsHeld.count > 0)
      items.push({
        key: `${r.currency}-held`,
        currency: r.currency,
        text: `${u.septlyDepositsHeld.count} held Septly deposit(s) have no house-merchant manual-review row, so they are invisible on the merchant page. The same backfill adds them.`,
      });
    if (u.oneSidedPayouts.count > 0)
      items.push({
        key: `${r.currency}-payout`,
        currency: r.currency,
        text: `${u.oneSidedPayouts.count} withdrawal(s) (${formatCurrencyAmount(u.oneSidedPayouts.amount, r.currency)}) were debited from the consumer but never reached the business side. See Orphaned Payouts.`,
      });
    if (u.declinedAfterDispatch.count > 0)
      items.push({
        key: `${r.currency}-declined`,
        currency: r.currency,
        text: `${u.declinedAfterDispatch.count} payout(s) (${formatCurrencyAmount(u.declinedAfterDispatch.amount, r.currency)}) were declined and refunded to the consumer after a provider payout existed, so the house merchant is still debited. Check each at the provider before correcting.`,
      });
  }
  return items;
};

// Nigerian time (UTC+1): a picked day starts at its local midnight, like the
// 4 October cut-off the conversion columns use.
const OCT_4 = "2026-10-04";
const sinceFromDay = (day) => (day ? new Date(`${day}T00:00:00+01:00`).toISOString() : undefined);

function ConsumerHouseReconciliation() {
  const [day, setDay] = useState(OCT_4);
  const { data, isLoading, isFetching, isError, refetch } = useConsumerHouseReconciliation(sinceFromDay(day));
  const { collapsed, toggle } = useCollapsed("treasury-consumer-house-reconciliation");
  const toInvestigate = data ? investigateItems(data.rows).length : 0;

  return (
    <div className={`mb-4 rounded border border-gray-200 bg-gray-50/50 ${collapsed ? "p-3" : "p-4"}`}>
      <div className={`flex gap-2 items-center text-sm font-bold text-[#013941] ${collapsed ? "" : "mb-3"}`}>
        <button
          onClick={toggle}
          className="flex gap-2 items-center font-bold"
          aria-expanded={!collapsed}
          title={collapsed ? "Show what explains the gap" : "Hide what explains the gap"}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <SearchCheck className="w-4 h-4" />
          What explains the gap
          {collapsed && toInvestigate > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold text-amber-800 bg-amber-100 rounded-full">
              {toInvestigate} to investigate
            </span>
          )}
        </button>
        {data?.generatedAt && !isFetching && (
          <span className="text-xs font-normal text-gray-400">
            calculated {new Date(data.generatedAt).toLocaleTimeString()}
          </span>
        )}
        {!collapsed && (
          <div className="flex gap-2 items-center ml-auto text-xs font-normal text-gray-500">
            <span>Movements since</span>
            <input
              type="date"
              value={day}
              min={OCT_4}
              // Nothing before 4 Oct is shown: the house merchant mirror did
              // not exist, so earlier movements cannot be explained from here.
              onChange={(e) => setDay(e.target.value && e.target.value >= OCT_4 ? e.target.value : OCT_4)}
              className="px-2 py-1 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              onClick={() => setDay(OCT_4)}
              title="Back to the start (4 Oct 2026)"
              className={`px-2 py-1 font-medium rounded-lg ${day === OCT_4 ? "text-white bg-[#013941]" : "text-[#013941] bg-white border border-gray-200"}`}
            >
              4 Oct
            </button>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex gap-1 items-center text-xs font-medium text-[#013941] hover:underline disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        )}
      </div>

      {collapsed ? null : isLoading ? (
        <p className="flex gap-2 items-center text-sm text-gray-500">
          <Loader className="w-4 h-4 animate-spin" /> Checking consumer and house-merchant movements...
        </p>
      ) : isError ? (
        <p className="text-sm text-red-600">Could not load the reconciliation. Try Refresh.</p>
      ) : (
        <>
          {data.filtered && (
            <p className="mb-2 text-xs text-gray-600">
              Explanation columns show movements since {new Date(data.since).toLocaleDateString()} only. The gap is
              the live balance difference and cannot be dated, so Unexplained is no longer comparable while a date
              is set.
            </p>
          )}
          {data.errors?.length > 0 && (
            <p className="mb-2 text-xs text-amber-700">
              Some checks failed and are missing: {data.errors.join("; ")}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs tracking-wider text-left text-gray-500 uppercase">
                  {[
                    "Currency",
                    "Consumers hold",
                    "House merchant holds",
                    "Gap",
                    "Unexplained",
                    "Conversions not mirrored since 4 Oct (net effect)",
                    "Septly deposits credited, no row",
                    "Septly deposits held, no row",
                    "Withdrawals debited, never sent",
                    "Declined after sending",
                    "Payout fee differences",
                    "Mirrored conversion drift",
                  ].map((h) => (
                    <th key={h} className="px-3 py-2 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r) => (
                  <tr key={r.currency}>
                    <td className="px-3 py-2 font-medium text-gray-900">{r.currency}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{formatCurrencyAmount(r.consumerOwed, r.currency)}</td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {r.houseMerchantBalance === null ? (
                        <span className="text-gray-400" title="The house merchant has no wallet in this currency">
                          no wallet
                        </span>
                      ) : (
                        formatCurrencyAmount(r.houseMerchantBalance, r.currency)
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.gap < 0 ? "text-red-600" : "text-green-700"}`}>
                      {formatCurrencyAmount(r.gap, r.currency)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-semibold ${Math.abs(r.unexplainedGap) < 0.01 ? "text-gray-400" : "text-amber-700"}`}
                      title="What is left of the gap after the unmirrored movements. Fees and spread Novacrust keeps, and rewards it funds, also end up here."
                    >
                      {formatCurrencyAmount(r.unexplainedGap, r.currency)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {r.unmirrored.conversionCount > 0
                        ? `${formatCurrencyAmount(r.unmirrored.conversionNet, r.currency)} (${r.unmirrored.conversionCount} trades)`
                        : "-"}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{cell(r.unmirrored.septlyDepositsCredited, r.currency)}</td>
                    <td className="px-3 py-2 text-gray-700">{cell(r.unmirrored.septlyDepositsHeld, r.currency)}</td>
                    <td className="px-3 py-2 text-gray-700">{cell(r.unmirrored.oneSidedPayouts, r.currency)}</td>
                    <td className="px-3 py-2 text-gray-700">{cell(r.unmirrored.declinedAfterDispatch, r.currency)}</td>
                    <td
                      className="px-3 py-2 text-gray-700"
                      title="Consumer fees minus house-merchant fees on payouts where they differed. Explained, not an error; payouts from the fee-floor release on no longer add to it."
                    >
                      {cell(r.unmirrored.payoutFeeDifference ?? { count: 0, amount: 0 }, r.currency)}
                    </td>
                    <td
                      className="px-3 py-2 text-gray-700"
                      title="House merchant credit minus what the consumer received, on conversions mirrored since 4 Oct. Positive = spread kept; the brackets show conversions where the house merchant got less."
                    >
                      {r.unmirrored.conversionDrift?.count > 0
                        ? `${formatCurrencyAmount(r.unmirrored.conversionDrift.amount, r.currency)} (${r.unmirrored.conversionDrift.count})${
                            r.unmirrored.conversionDrift.shortCount > 0
                              ? `, ${r.unmirrored.conversionDrift.shortCount} short`
                              : ""
                          }`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            const items = investigateItems(data.rows);
            if (items.length === 0) return null;
            return (
              <div className="mt-3">
                <div className="mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">To investigate</div>
                <ul className="space-y-1">
                  {items.map((i) => (
                    <li key={i.key} className="text-sm text-gray-700">
                      <strong>{i.currency}</strong>: {i.text}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
          <p className="mt-2 text-xs text-gray-400">
            Fees and spread Novacrust keeps make the house merchant hold more than consumers, and rewards Novacrust
            funds make it hold less. Those are expected. The conversion figure only counts conversions made since 4 October, when the mirror went live; earlier ones stay in the unexplained part of the gap.
          </p>
        </>
      )}
    </div>
  );
}

export default ConsumerHouseReconciliation;
